<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
    import { Input } from '@/components/ui/input'
    import { cn } from '@/lib/utils'
    import { sortDropdownOptions } from '@/utils/globals'
    const props = defineProps<{
      modelValue: string
      options: string[]
      placeholder?: string
      class?: string
      dropdownClass?: string
      required?: boolean
      id?: string
    }>()
    
    const emit = defineEmits<{
      'update:modelValue': [value: string]
    }>()
    
    const isOpen = ref(false)
    const searchQuery = ref('')
    const containerRef = ref<HTMLElement>()
    const selectedOnPointerDown = ref(false)
    const CLOSE_ALL_EVENT = 'combobox:close-all'
    
    // Filter options based on search query
    const sortedOptions = computed(() => sortDropdownOptions(props.options))

    const filteredOptions = computed(() => {
      if (!searchQuery.value) return sortedOptions.value
      const query = searchQuery.value.toLowerCase()
      return sortedOptions.value.filter(option =>
        option.toLowerCase().includes(query)
      )
    })
    
    // Update search query when modelValue changes externally
    watch(() => props.modelValue, (newValue) => {
      searchQuery.value = newValue || ''
    })
    
    // Initialize search query with current value
    searchQuery.value = props.modelValue || ''
    
    // Handle input change
    function onInput(event: Event) {
      const target = event.target as HTMLInputElement
      searchQuery.value = target.value
      isOpen.value = true
      emit('update:modelValue', target.value)
    }
    
    // Handle option selection
    function selectOption(option: string) {
      searchQuery.value = option
      emit('update:modelValue', option)
      isOpen.value = false
    }

    function onOptionPointerDown(option: string) {
      selectedOnPointerDown.value = true
      selectOption(option)
      window.setTimeout(() => {
        selectedOnPointerDown.value = false
      }, 250)
    }

    function onOptionClick(option: string) {
      if (selectedOnPointerDown.value) {
        selectedOnPointerDown.value = false
        return
      }

      selectOption(option)
    }
    
    // Handle input focus
    function onFocus() {
      isOpen.value = true
    }
    
    function closeDropdown() {
      isOpen.value = false
      // Reset search query to current modelValue if dropdown closes without selection
      if (props.modelValue !== searchQuery.value) {
        searchQuery.value = props.modelValue || ''
      }
    }

    function onDocumentPointerDown(event: PointerEvent) {
      const target = event.target as Node | null
      if (!target) return
      if (containerRef.value?.contains(target)) return

      closeDropdown()
    }

    function onCloseAllComboboxes() {
      closeDropdown()
    }

    onMounted(() => {
      document.addEventListener('pointerdown', onDocumentPointerDown)
      window.addEventListener(CLOSE_ALL_EVENT, onCloseAllComboboxes)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('pointerdown', onDocumentPointerDown)
      window.removeEventListener(CLOSE_ALL_EVENT, onCloseAllComboboxes)
    })
    </script>
    
    <template>
      <div ref="containerRef" class="relative w-full">
        <Input
          :id="id"
          :model-value="searchQuery"
          @input="onInput"
          @focus="onFocus"
          :placeholder="placeholder"
          :class="cn('w-full', props.class)"
          :required="required"
        />
        <div
          v-if="isOpen && filteredOptions.length > 0"
          :class="cn('absolute left-0 right-0 top-full z-[2000] mt-1 bg-white border border-input rounded-md shadow-lg max-h-60 overflow-auto', props.dropdownClass)"
          @pointerdown.stop
          @mousedown.stop
          @click.stop
        >
          <div
            v-for="option in filteredOptions"
            :key="option"
            class="px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm"
            :class="{
              'bg-accent text-accent-foreground': option === modelValue
            }"
            @pointerdown.prevent.stop="onOptionPointerDown(option)"
            @click.prevent.stop="onOptionClick(option)"
          >
            {{ option }}
          </div>
        </div>
        <div
          v-if="isOpen && filteredOptions.length === 0 && searchQuery"
          :class="cn('absolute left-0 right-0 top-full z-[2000] mt-1 bg-white border border-input rounded-md shadow-lg', props.dropdownClass)"
          @pointerdown.stop
          @mousedown.stop
          @click.stop
        >
          <div class="px-3 py-2 text-sm text-muted-foreground">
            Keine Ergebnisse gefunden
          </div>
        </div>
      </div>
    </template>
